import { Injectable } from '@angular/core';

import type { Observable } from 'rxjs';

import { injectReportsHttpApiService } from '@core/reports-contracts';
import type {
  ReportDashboardComponent,
  ReportDashboardComponentCreate,
  ReportDashboardComponentCreateRequest,
  ReportsComponentHttpClientService,
} from '@core/reports-types';

const dropComponentLocalPropsBeforeSave = (
  component: ReportDashboardComponentCreate | ReportDashboardComponent,
) => {
  if ('id' in component) {
    const { id, ...result } = component;
    return result;
  }

  return component;
};

/**
 * Abstract base class for reports components HTTP client.
 *
 * @deprecated This abstract class exists for backward compatibility with domain-specific
 * implementations (e.g., RecruiteeReportsComponentsHttpClient). Once the migration to
 * tellent-analytics is complete for all services, this can be removed and domains can
 * use ReportsComponentsHttpClient directly via dependency injection.
 */
export abstract class ReportsComponentsHttpClientBase implements ReportsComponentHttpClientService {
  private readonly http = injectReportsHttpApiService();

  public createOne(
    dashboardId: number,
    component: ReportDashboardComponentCreateRequest,
  ): Observable<{ component: ReportDashboardComponent }> {
    return this.http.post(`/dashboards/${dashboardId}/components`, null, {
      component: dropComponentLocalPropsBeforeSave(component),
    });
  }

  public updateOne(
    dashboardId: number,
    component: ReportDashboardComponent,
  ): Observable<{ component: ReportDashboardComponent }> {
    return this.http.patch(`/dashboards/${dashboardId}/components/${component.id}`, null, {
      component,
    });
  }

  public updateGridProps(
    dashboardId: number,
    components: Array<Pick<ReportDashboardComponent, 'id' | 'grid'>>,
  ): Observable<{ components: ReportDashboardComponent[] }> {
    return this.http.patch<{ components: ReportDashboardComponent[] }>(
      `/dashboards/${dashboardId}/components/update_positions`,
      null,
      { components },
    );
  }

  public removeOne(
    dashboardId: number,
    componentId: number | string,
  ): Observable<{ component: ReportDashboardComponent }> {
    return this.http.delete(`/dashboards/${dashboardId}/components/${componentId}`);
  }
}

@Injectable()
export class ReportsComponentsHttpClient
  extends ReportsComponentsHttpClientBase
  implements ReportsComponentHttpClientService {}
